import React, { useState } from 'react'
import Icon from '../AppIcon'
import { useOperator } from '../../lib/OperatorContext'
import PinKeypad from './PinKeypad'

/**
 * Per-action operator PIN gate (shop-app parity): a modal that verifies the
 * PIN server-side (operatorPinVerify — same throttling/lockout as the lock
 * screen) and hands the verified operator to `onVerified({ id, name })`.
 * Uses the side-effect-free `checkPin` so a second person confirming a
 * transaction does NOT replace the portal-wide operator session.
 *
 * `mockOperator`: when set (local mock mode, no server writes), any
 * well-formed PIN resolves to it so the flow stays testable offline.
 */
export default function OperatorPinDialog({
  title = 'Operator PIN',
  subtitle,
  submitLabel = 'Verify',
  mockOperator = null,
  onVerified,
  onCancel,
}) {
  const { checkPin } = useOperator()
  // Cancel must not race an in-flight verification: a verify that resolves
  // after cancel would fire onVerified anyway (stale-closure save).
  const [busy, setBusy] = useState(false)

  const handleSubmit = async (pin) => {
    if (!/^\d{4}$/.test(pin)) throw new Error('Enter your 4-digit operator PIN')
    if (mockOperator) {
      onVerified(mockOperator)
      return
    }
    const result = await checkPin(pin)
    if (!result?.operatorId) throw new Error('Invalid PIN')
    onVerified({ id: result.operatorId, name: result.name })
  }

  return (
    <div
      className="fixed inset-0 z-[95] bg-black/60 flex items-center justify-center px-4 overflow-y-auto overscroll-contain"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className="w-full max-w-xs bg-background rounded-2xl border border-border/60 shadow-elevation-4 px-5 py-6 text-center my-6">
        <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-3">
          <Icon name="KeyRound" size={22} className="text-primary" />
        </div>
        <h2 className="text-lg font-semibold text-foreground">{title}</h2>
        {subtitle ? (
          <p className="text-sm text-muted-foreground mt-1 mb-5">{subtitle}</p>
        ) : (
          <div className="mb-5" />
        )}

        <PinKeypad minLength={4} maxLength={4} onSubmit={handleSubmit} submitLabel={submitLabel} onBusyChange={setBusy} />

        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="mt-4 text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40"
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  )
}
